import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { IconMaterial } from '@uhg-abyss/web/ui/IconMaterial';
import { usePropsContext } from '@uhg-abyss/web/hooks/usePropsContext';
import { Badge } from '@uhg-abyss/web/ui/Badge';
import { IconSymbol } from '@uhg-abyss/web/ui/IconSymbol';
import { Layout } from '@uhg-abyss/web/ui/Layout';
import { Accordion } from '@uhg-abyss/web/ui/Accordion';
import { ProviderTableData } from '../../../common/ProviderTableData';
import { ProviderTableSearch } from '../../../common/ProviderTableSearch';
import {
  StyledAccordionContent,
  StyledAccordionTrigger,
  StyledAccordionHeader,
  StyledTableRow,
  StyledTableDataCheckMarkColumn,
  StyledTableDataLabelColumn,
  StyledTableDataBadgeColumn,
  StyledErrorMessage,
} from '../../../common/StyledComponents/StyledComponents';

const careProviderTableSearchOptions = [
  { value: 'All', label: 'All' },
  { value: 'Active', label: 'Active' },
  { value: 'Inactive', label: 'Inactive' },
];

const careProviderColWidth = ['10%', '60%', '30%'];

const errorMessage = 'Unable to load care providers';

export const CareProvider = ({
  careProviders,
  selectedProvider,
  setSelectedProvider,
  isCareProvidersLoading,
  isCareProviderAccordionExpanded,
  setExpandedValue,
  isTaxIdRowClicked,
  setIsTaxIdRowClicked,
  setIsOwnerRowClicked,
  careProvidersError,
  setIsSaveDisabled,
  setIsCareProviderAccordionDisabled,
  isAccordionDisabled,
}) => {
  const { events } = usePropsContext();
  const { servicingProviderName, servicingProviderMpin } = selectedProvider;
  const [providerSearchValue, setProviderSearchValue] = useState('');
  const [providerStatusFilter, setProviderStatusFilter] = useState('All');
  const [selectedProviderRow, setProviderSelectedRow] = useState(
    `${servicingProviderName} (${servicingProviderMpin})`
  );
  const [selectedCareProvider, setSelectedCareProvider] = useState(
    `${servicingProviderName} (${servicingProviderMpin})`
  );

  useEffect(() => {
    if (!Array.isArray(careProviders) || careProviders.length > 0) {
      setIsCareProviderAccordionDisabled(false);
    } else {
      setIsCareProviderAccordionDisabled(true);
    }
  }, [careProviders]);

  useEffect(() => {
    if (!servicingProviderName) {
      setSelectedCareProvider('');
      setProviderSelectedRow('');
    }
  }, [selectedProvider]);

  useEffect(() => {
    if (isTaxIdRowClicked) {
      setSelectedCareProvider('');
      setProviderSelectedRow('');
      setProviderSearchValue('');
      setProviderStatusFilter('All');
    }
  }, [isTaxIdRowClicked, selectedProvider.taxIdNumber]);

  const filteredProviderData = careProviders.filter(
    (item) =>
      item.label.toLowerCase().includes(providerSearchValue.toLowerCase()) &&
      (providerStatusFilter === 'All' ||
        (providerStatusFilter === 'Active' && item.isActive) ||
        (providerStatusFilter === 'Inactive' && !item.isActive))
  );

  let subText = '';
  if (careProvidersError && !isCareProviderAccordionExpanded) {
    subText = (
      <Layout.Group>
        <IconSymbol icon="error" color="$error1" variant="outlined" />
        <StyledErrorMessage>{errorMessage}</StyledErrorMessage>
      </Layout.Group>
    );
  } else if (!careProvidersError) {
    subText = selectedCareProvider;
  }

  const handleCareProviderChange = (newCareProviderMpin) => {
    // check if selection is same as initial
    if (newCareProviderMpin !== selectedProvider?.servicingProviderMpin) {
      const careProviderMatch = (careProviders || []).find(
        (careProvider) => careProvider.mpin === newCareProviderMpin
      );
      setSelectedProvider((prevSelection) => ({
        ...prevSelection,
        servicingProviderMpin: careProviderMatch.mpin,
        servicingProviderName: careProviderMatch.name,
        servicingProviderFirstName: careProviderMatch.firstName,
        servicingProviderLastName: careProviderMatch.lastName,
        isActive: careProviderMatch.isActive,
      }));
    }
    setIsSaveDisabled(false);
    events.onCareProviderChange(newCareProviderMpin);
  };

  const handleProviderRowClick = (item) => {
    if (selectedProviderRow === item.label) {
      return;
    }
    setProviderSelectedRow(item.label);
    setSelectedCareProvider(item.label);
    setExpandedValue('');
    setProviderSearchValue('');
    setProviderStatusFilter('All');
    setIsOwnerRowClicked(false);
    setIsTaxIdRowClicked(false);
    handleCareProviderChange(item.mpin);
  };

  const renderRow = (data) => {
    return data.map((item) => {
      return (
        <React.Fragment>
          <StyledTableRow
            key={item.label}
            onClick={() => handleProviderRowClick(item)}
          >
            <StyledTableDataCheckMarkColumn>
              <If condition={selectedProviderRow === item.label}>
                <IconMaterial size="22px" icon="check" />
              </If>
            </StyledTableDataCheckMarkColumn>
            <StyledTableDataLabelColumn
              data-testid="care-provider-table-row"
              isSelectedRow={selectedProviderRow === item.label}
            >
              {item.label}
            </StyledTableDataLabelColumn>
            <StyledTableDataBadgeColumn data-testid="care-provider-status-badge">
              <If condition={!item.isActive}>
                <Badge variant="warning">
                  <StyledTableDataBadgeColumn>
                    Inactive
                  </StyledTableDataBadgeColumn>
                </Badge>
              </If>
            </StyledTableDataBadgeColumn>
          </StyledTableRow>
        </React.Fragment>
      );
    });
  };

  renderRow(filteredProviderData);

  return (
    <React.Fragment>
      <StyledAccordionTrigger isDisabled={isAccordionDisabled}>
        <StyledAccordionHeader isError={careProvidersError} subText={subText}>
          Care Provider
        </StyledAccordionHeader>
      </StyledAccordionTrigger>
      <Accordion.Content>
        <StyledAccordionContent>
          <ProviderTableSearch
            searchValue={providerSearchValue}
            setSearchValue={setProviderSearchValue}
            searchFilter={providerStatusFilter}
            setSearchFilter={setProviderStatusFilter}
            options={careProviderTableSearchOptions}
            isCareProvider
            isDisabled={isCareProvidersLoading}
            isError={careProvidersError}
          />
          <ProviderTableData
            colWidths={careProviderColWidth}
            data={filteredProviderData}
            renderRow={renderRow}
            isLoading={isCareProvidersLoading}
            ariaLoadingLabel="Loading Care Providers"
            isError={careProvidersError}
            errorMessage={errorMessage}
          />
        </StyledAccordionContent>
      </Accordion.Content>
    </React.Fragment>
  );
};

CareProvider.propTypes = {
  selectedProvider: PropTypes.shape({
    organizationId: PropTypes.string,
    corporateProviderMpin: PropTypes.string,
    taxIdNumber: PropTypes.string,
    uhcProviderId: PropTypes.string,
    userId: PropTypes.string,
    uniqueId: PropTypes.string,
    servicingProviderMpin: PropTypes.string,
    servicingProviderName: PropTypes.string,
    isThirdParty: PropTypes.bool,
  }),
  setSelectedProvider: PropTypes.func.isRequired,
  careProviders: PropTypes.arrayOf(PropTypes.shape({})),
  isCareProvidersLoading: PropTypes.bool,
  setExpandedValue: PropTypes.func.isRequired,
  isTaxIdRowClicked: PropTypes.bool.isRequired,
  setIsTaxIdRowClicked: PropTypes.func.isRequired,
  setIsOwnerRowClicked: PropTypes.func.isRequired,
  careProvidersError: PropTypes.bool,
  isCareProviderAccordionExpanded: PropTypes.bool.isRequired,
  setIsSaveDisabled: PropTypes.func.isRequired,
  setIsCareProviderAccordionDisabled: PropTypes.func,
  isAccordionDisabled: PropTypes.bool,
};

CareProvider.defaultProps = {
  selectedProvider: null,
  careProviders: [],
  isCareProvidersLoading: false,
  careProvidersError: false,
  setIsCareProviderAccordionDisabled: () => {},
  isAccordionDisabled: false,
};
